import type { AppEnv } from "@/config/env";

/**
 * When the drafting engine may warm up before anyone opens a drawing (`schedule.ts`). Warming fetches about 5 MB
 * (the engine's WebAssembly, compressed) that Open Draft would fetch anyway, so it waits for a quiet moment and is
 * left out where data is dear: a connection that asks to save data, or one of the slow kinds the settings name.
 */

export interface WarmupConfig {
  readonly enabled: boolean;
  /** How long after a signed-in page mounts the warm-up waits before it asks for an idle moment, in ms. */
  readonly delayMs: number;
  /** `navigator.connection.effectiveType` values on which no warm-up runs. */
  readonly skipNetworks: ReadonlySet<string>;
  /** Where the engine runs: only a worker is warmed, so the main thread is never held up compiling. */
  readonly host: AppEnv["VITE_DRAFT_HOST"];
}

/** What the browser says of its connection (the Network Information API, where it has one). */
export interface ConnectionHints {
  readonly saveData?: boolean | undefined;
  readonly effectiveType?: string | undefined;
}

export type SkipReason = "off" | "in-process" | "save-data" | "slow-network";

export function warmupConfigOf(
  env: Pick<
    AppEnv,
    | "VITE_DRAFT_WARMUP"
    | "VITE_DRAFT_WARMUP_DELAY_MS"
    | "VITE_DRAFT_WARMUP_SKIP_NETWORKS"
    | "VITE_DRAFT_HOST"
  >,
): WarmupConfig {
  return {
    enabled: env.VITE_DRAFT_WARMUP === "on",
    delayMs: env.VITE_DRAFT_WARMUP_DELAY_MS,
    skipNetworks: new Set(
      env.VITE_DRAFT_WARMUP_SKIP_NETWORKS.split(",")
        .map((name) => name.trim())
        .filter((name) => name.length > 0),
    ),
    host: env.VITE_DRAFT_HOST,
  };
}

/** Why no warm-up runs here, or `undefined` when it may. */
export function warmupSkipReason(
  config: WarmupConfig,
  connection: ConnectionHints | undefined,
): SkipReason | undefined {
  if (!config.enabled) return "off";
  if (config.host !== "worker") return "in-process";
  if (connection?.saveData === true) return "save-data";
  const type = connection?.effectiveType;
  if (type !== undefined && config.skipNetworks.has(type)) return "slow-network";
  return undefined;
}
